<template>
  <expansion-panel
    key="expansion-panel-offer-status"
    :hideDivider="panelHideDivider"
    :readonly="panelReadonly"
    :title="$t('offer-manager.create.step-offer-status.title')">
    <template #title-expand>
      <div @click.stop="openCloseInfoDialog('offer-status')">
        <v-icon class="fa-light fa-circle-info ml-1 px-2 click" color="tertiary" dense />
      </div>
    </template>
    <template v-slot:panel-content>
      <div class="d-flex flex-column align-center m-auto">
        <m-radio-group
          class="max-content"
          :items="filterList"
          :rules="statusRules"
          :value="status"
          data-cy="offerStatusOffer"
          row
          @change="value => updateOffer('setStatus', value)" />
        <m-radio-group
          id="automatic-status"
          class="mt-0"
          :items="statusDisabledList"
          :value="status"
          data-cy="offerStatusDisabledOffer"
          row
          disabled />
      </div>

      <mapit-dialog :dialog="dialog" @close="openCloseInfoDialog">
        <template v-slot:header-text>
          <span>
            {{ $t(`offer-manager.create.step-offer-status.${infoVariant}.title`) }}
          </span>
        </template>
        <template v-slot:body>
          <div class="mb-6" v-html="$t(`offer-manager.create.step-offer-status.${infoVariant}.description`)" />
        </template>
      </mapit-dialog>
    </template>
  </expansion-panel>
</template>

<script lang="ts">
import {Status} from '@muvio/honda-offers-builder/model'
import {computed, defineComponent, ref, watch} from '@vue/composition-api'

import MapitDialog from '@/components/dialog/MapitDialog.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
// @ts-ignore
import {statusRules} from '@/helpers/offer-manager-rules'
import {InputTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'

type RadioButtonItem = {
  label: string
  value: any
  disabled?: boolean
}

export default defineComponent({
  name: 'StatusData',
  components: {ExpansionPanel, MapitDialog},
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    panelReadonly: {
      type: Boolean,
      default: false,
    },
    panelHideDivider: {
      type: Boolean,
      default: false,
    },
    forceStatus: {
      type: String,
      default: null,
    },
  },
  setup(props, {root, emit}) {
    const dialog = ref<boolean>(false)
    const infoVariant = ref<string>('offer-status')
    const filterList = ref<RadioButtonItem[]>(
      Object.values([Status.Pending, Status.Accepted, Status.Refused]).map(item => ({
        label: root.$t(`offer-manager.create.step-offer-status.lbl-offer-status-${item}`).toString(),
        value: item,
      })),
    )
    const statusDisabledList = ref<RadioButtonItem[]>(
      Object.values([Status.COICreated, Status.Quote, Status.Ordered, Status.Invoiced, Status.Completed]).map(item => ({
        label: root.$t(`offer-manager.create.step-offer-status.lbl-offer-status-${item}`).toString(),
        value: item,
        disabled: true,
      })),
    )

    const {getStatus: status, getInitialStatus: initialStatus} = mapGetters(root.$store.getters)
    const isPendingOffer = computed(() => status.value === Status.Pending)

    function updateOffer(action: string, value: any) {
      root.$store.dispatch(action, value)
      emit('validateForm')
    }

    function openCloseInfoDialog(variant: string) {
      dialog.value = !dialog.value
      infoVariant.value = variant
    }

    function setStatusList(list: RadioButtonItem[], statusValue: Status, forced: boolean) {
      if (forced) {
        filterList.value = list.map((item: RadioButtonItem) => {
          item.disabled = item.value !== statusValue
          return item
        })
        return
      }

      switch (statusValue) {
        case Status.Accepted:
          filterList.value = list.map((item: RadioButtonItem) => {
            item.disabled = item.value !== Status.Refused && statusValue !== item.value // Solo enabled el actual y refused
            return item
          })
          break
        case Status.COICreated:
        case Status.Completed:
        case Status.Refused:
          filterList.value = list.map((item: RadioButtonItem) => {
            item.disabled = statusValue !== item.value // Solo enabled el actual
            return item
          })
          break
        case Status.Pending:
        default:
          filterList.value = list.map((item: RadioButtonItem) => {
            // Por defecto, habilitados Accepted, Refusedm y Pending
            item.disabled =
              item.value !== Status.Refused && item.value !== Status.Accepted && statusValue !== item.value
            return item
          })
          break
      }
    }

    const forceStatusComputed = computed(() => props.forceStatus)

    watch([initialStatus, forceStatusComputed], () => {
      setStatusList(
        filterList.value,
        forceStatusComputed.value || initialStatus.value,
        forceStatusComputed.value != null,
      )
    })

    return {
      InputTypes,
      statusRules,
      status,
      dialog,
      infoVariant,
      isPendingOffer,
      filterList,
      updateOffer,
      openCloseInfoDialog,
      statusDisabledList,
    }
  },
})
</script>
<style>
#automatic-status [role='radiogroup'] {
  row-gap: 12px !important;
  justify-content: center !important;
}
</style>
<style scoped>
.theme--light.v-radio--is-disabled .v-icon {
  color: #dbdbdb !important;
}

.max-content {
  width: max-content;
}
</style>
